import { z } from 'zod'

export type SlackShareKind = 'meeting' | 'chat'

/** The slice of the gateway's Slack workspace artifact the destination picker reads. */
const slackDirectorySchema = z.object({
  workspace: z.object({
    name: z.string(),
    currentUser: z.object({ userId: z.string() }),
  }),
  channels: z.array(z.object({ id: z.string(), name: z.string(), isPrivate: z.boolean() })),
  users: z.array(
    z.object({
      id: z.string(),
      username: z.string(),
      displayName: z.string(),
      realName: z.string().nullable(),
      image48: z.string().nullable(),
    }),
  ),
})

export type SlackDirectory = z.infer<typeof slackDirectorySchema>

export const slackArtifactResponseSchema = z.object({
  data: z.object({ artifactData: slackDirectorySchema.nullable() }),
})

export const slackShareResponseSchema = z.object({
  data: z.object({ channelId: z.string(), ts: z.string(), permalink: z.string().nullable() }),
})

export type SlackShareResult = z.infer<typeof slackShareResponseSchema>['data']

export interface SlackChannelDestination {
  type: 'channel'
  id: string
  name: string
  isPrivate: boolean
}

export interface SlackPersonDestination {
  type: 'user'
  id: string
  name: string
  /** The real name, when the display name differs from it. */
  detail: string | null
  username: string
  image: string | null
}

export type SlackDestination = SlackChannelDestination | SlackPersonDestination

// A type alias rather than an interface: Base UI's grouped-items type carries an index signature,
// which only an alias satisfies implicitly.
export type SlackDestinationGroup = {
  label: 'Channels' | 'People'
  items: Array<SlackDestination>
}

/** Keeps a large workspace's people list cheap to render; search narrows it to the rest. */
export const MAX_DESTINATIONS_PER_GROUP = 50

const byName = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })

export function toSlackDestinations(directory: SlackDirectory): Array<SlackDestination> {
  const channels: Array<SlackChannelDestination> = directory.channels
    .map((channel) => ({ type: 'channel' as const, id: channel.id, name: channel.name, isPrivate: channel.isPrivate }))
    .sort(byName)

  const people: Array<SlackPersonDestination> = directory.users
    .filter((user) => user.id !== directory.workspace.currentUser.userId)
    .map((user) => {
      const name = user.displayName || user.realName || user.username
      return {
        type: 'user' as const,
        id: user.id,
        name,
        detail: user.realName && user.realName !== name ? user.realName : null,
        username: user.username,
        image: user.image48,
      }
    })
    .sort(byName)

  return [...channels, ...people]
}

function searchableNames(destination: SlackDestination): Array<string> {
  if (destination.type === 'channel') {
    return [destination.name]
  }
  return [destination.name, destination.detail ?? '', destination.username]
}

/**
 * Groups destinations into channels and people, keeping those whose name contains the query.
 * Names that start with it rank first; a leading `#` or `@` is ignored, since that's how people
 * type a channel or a person.
 */
export function filterSlackDestinations(
  destinations: Array<SlackDestination>,
  query: string,
  limit = MAX_DESTINATIONS_PER_GROUP,
): Array<SlackDestinationGroup> {
  const needle = query.trim().replace(/^[#@]/, '').toLowerCase()

  const matches = needle
    ? destinations
        .map((destination) => {
          const names = searchableNames(destination).map((name) => name.toLowerCase())
          const rank = names.some((name) => name.startsWith(needle))
            ? 0
            : names.some((name) => name.includes(needle))
              ? 1
              : null
          return { destination, rank }
        })
        .filter((entry): entry is { destination: SlackDestination; rank: number } => entry.rank !== null)
        .sort((a, b) => a.rank - b.rank)
        .map((entry) => entry.destination)
    : destinations

  const groups: Array<SlackDestinationGroup> = [
    { label: 'Channels', items: matches.filter((d) => d.type === 'channel').slice(0, limit) },
    { label: 'People', items: matches.filter((d) => d.type === 'user').slice(0, limit) },
  ]
  return groups.filter((group) => group.items.length > 0)
}

export function slackDestinationLabel(destination: SlackDestination): string {
  return destination.type === 'channel' ? `#${destination.name}` : destination.name
}

/** Base UI's combobox hands values back untyped. */
export function isSlackDestination(value: unknown): value is SlackDestination {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    (value.type === 'channel' || value.type === 'user') &&
    'id' in value &&
    typeof value.id === 'string'
  )
}

export function isSameSlackDestination(a: SlackDestination, b: SlackDestination): boolean {
  return a.type === b.type && a.id === b.id
}

export interface SlackShareRequest {
  kind: SlackShareKind
  id: string
  destination: { type: 'channel'; channelId: string } | { type: 'user'; userId: string }
  message?: string
}

export function buildSlackShareRequest(input: {
  kind: SlackShareKind
  id: string
  destination: SlackDestination
  message: string
}): SlackShareRequest {
  const message = input.message.trim()
  return {
    kind: input.kind,
    id: input.id,
    destination:
      input.destination.type === 'channel'
        ? { type: 'channel', channelId: input.destination.id }
        : { type: 'user', userId: input.destination.id },
    ...(message ? { message } : {}),
  }
}

/** The `sharedConversationId` in a chat share link (`https://highlightai.com/share/<id>?env=…`). */
export function sharedConversationIdFromLink(link: string): string | null {
  try {
    const match = new URL(link).pathname.match(/^\/share\/([^/]+)\/?$/)
    return match ? match[1] : null
  } catch {
    return null
  }
}

const slackShareErrorSchema = z.object({
  error: z.string().optional(),
  errorCode: z.string().optional(),
})

export const RECONNECT_SLACK_MESSAGE = 'Reconnect Slack in Settings → Integrations, then share again.'

export function describeSlackShareError(status: number, body: unknown): string {
  const parsed = slackShareErrorSchema.safeParse(body)
  const { error, errorCode } = parsed.success ? parsed.data : {}

  if (errorCode === 'CONNECTION_REQUIRED') {
    return RECONNECT_SLACK_MESSAGE
  }
  if (errorCode === 'VALIDATION_FAILED' && error) {
    return error
  }
  if (status === 404) {
    return "This can't be shared to Slack right now."
  }
  return "Couldn't share to Slack. Try again."
}
